import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, Button } from '@ui/button';
import { RoundedCheckbox, MultiSelect } from '@web/atoms';
import { useShopifyCart } from '@web/shopify';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { StyledFormWithImage } from '../StyledForm';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { useAllergies, useGetAllergies } from '../../allergies';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import { Question } from '../Question';
import {
  CheckboxContainer,
  MultiSelectContainer,
  FieldWrapper,
  ArrowContainer,
  ImageContainer,
  ButtonContainer,
  MobileImageContainer,
  DesktopImageContainer,
} from './styles';
import { schema } from './schema';
import { getPlaceholderAllergies } from './constants';

export { schema } from './schema';

const toggleFeaturedAllergy = (allergy, state) => {
  if (!findAllergyInState(allergy, state)) {
    return [...state, allergy];
  }
  return state.filter(a => a.value !== allergy.value);
};

const findAllergyInState = (allergy, state) =>
  state?.find(a => a.value === allergy.value);

const normalize = allergies =>
  allergies.map(allergy => ({
    label: allergy.name,
    value: allergy.id,
  }));

export const AllergySelectionForm = ({
  allergiesQuestionPrompt,
  allergiesQuestionCaption,
  defaultValues,
  goBack,
  onSubmit,
  index,
  allergySearchLabel,
  placeholderAllergiesCount,
  ctaButtonText = 'Continue',
  mobileImage,
  desktopImage,
}) => {
  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const getAllergies = useGetAllergies();
  const { cart } = useShopifyCart();
  const allergies = useAllergies();
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('pup_allergy_2', cart?.customer || customer),
    );
    goBack();
  };

  const isSubmittingOrSuccessful =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  const placeholderAllergies = getPlaceholderAllergies(
    placeholderAllergiesCount,
  );

  const featuredAllergies = normalize(
    allergies?.filter(item => item.featured) ?? placeholderAllergies,
  );

  const multiselectOnChange = multiselectedValues => [...multiselectedValues];

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_allergies',
        cart?.customer || customer,
        data,
      ),
    );
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledFormWithImage onSubmit={handleSubmit(submitForm)}>
      <ImageContainer>
        <ArrowContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isSubmittingOrSuccessful}
            type="button"
            variant="dark"
          />
        </ArrowContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper $position={'absolute'} $maxHeight={70}>
        <FormFieldsContainer $maxWidth={391}>
          <Question
            prompt={allergiesQuestionPrompt}
            subheadline={allergiesQuestionCaption}
            typography={'heading4'}
          >
            <FieldWrapper>
              <Controller
                control={control}
                name="allergies"
                render={({
                  field: { onChange, value: selectedAllergies = [] },
                }) => (
                  <>
                    <CheckboxContainer>
                      {featuredAllergies.map(allergy => (
                        <RoundedCheckbox
                          {...allergy}
                          key={allergy.value}
                          height={50}
                          variant="dark"
                          checked={
                            !!findAllergyInState(allergy, selectedAllergies)
                          }
                          onChange={() =>
                            onChange(
                              toggleFeaturedAllergy(allergy, selectedAllergies),
                            )
                          }
                          showIcon={
                            findAllergyInState(allergy, selectedAllergies)
                              ? 'check'
                              : null
                          }
                          label={allergy.label}
                        />
                      ))}
                    </CheckboxContainer>

                    <MultiSelectContainer>
                      <MultiSelect
                        name="other allergies"
                        selectedValues={selectedAllergies}
                        featuredValues={featuredAllergies}
                        values={getAllergies}
                        onChange={values =>
                          onChange(multiselectOnChange(values))
                        }
                        searchLabel={allergySearchLabel}
                      />
                    </MultiSelectContainer>
                  </>
                )}
              />
            </FieldWrapper>
          </Question>
          <ButtonContainer>
            <Button
              type="submit"
              aria-label={ctaButtonText}
              disabled={isSubmittingOrSuccessful}
              variant="sea2"
              isLoading={isSubmittingOrSuccessful}
              horizontalPadding={32}
            >
              {ctaButtonText}
            </Button>
          </ButtonContainer>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

AllergySelectionForm.displayName = 'AllergySelectionForm';

AllergySelectionForm.propTypes = {
  allergiesQuestionPrompt: PropTypes.string.isRequired,
  allergiesQuestionCaption: PropTypes.string,
  defaultValues: PropTypes.shape({
    selectedAllergies: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  allergySearchLabel: PropTypes.string.isRequired,
  placeholderAllergiesCount: PropTypes.number.isRequired,
  ctaButtonText: PropTypes.object,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
};
